// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { array } from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from '@carbon/react';

const StyledContainer = styled.div`
  margin-top: 25px;

  .cds--data-table {
    font-size: 16px;
    border: 1px solid #e2e2e2;
    overflow-x: scroll;

    thead th {
      color: #3a3a3a;
      font-size: 16px;
      font-weight: 600;
      background: #040F2A;
      color: #fff;

      &:first-of-type, &:last-of-type {
        .cds--table-header-label {
          text-align: center;
        }
      }

      .cds--table-header-label {
        font-size: 16px;
        font-weight: 500;
        text-align: center;
      }
    }

    tbody tr {
      height: 2rem;
      
      td {
        background-color: #fff;
        text-align: center;
      }
    }
  }
`;

const SupplierProductListTable = ({ products }) => {
  const { t } = useTranslation();

  const headerData = useMemo(
    () => [
      {
        key: 'supplier',
        header: t('common.supplier'),
      },
      {
        key: 'pnSupplier',
        header: t('products.pnSupplier'),
      },
      {
        key: 'productEan',
        header: t('products.productEan'),
      },
      {
        key: 'price',
        header: t('products.price'),
      },
      {
        key: 'ronPrice',
        header: t('products.ronPrice'),
      },
      {
        key: 'ronPriceWithVat',
        header: t('products.ronPriceWithVat'),
      },
      {
        key: 'cmp',
        header: t('products.cmp'),
      },
      {
        key: 'stock',
        header: t('product.stock'),
      },
      {
        key: 'gmPercentage',
        header: t('product.gmPercentage'),
      },
      {
        key: 'syncedAt',
        header: t('products.supplierListSyncedAt'),
      }
    ],
    [t]
  );

  const filteredHeaderData = useMemo(() => {
    const hasCmpField = products.some(product => Object.prototype.hasOwnProperty.call(product, 'cmp'));
    return hasCmpField
      ? headerData
      : headerData.filter(header => header.key !== 'cmp');
  }, [products, headerData]);

  return (
    <StyledContainer>
      <DataTable rows={ products } headers={ filteredHeaderData }>
        {({ rows, headers, getHeaderProps, getTableProps }) => (
          <TableContainer>
            <Table { ...getTableProps() }>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <TableHeader
                      { ...getHeaderProps({ header }) }
                    >
                      { header.header }
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                { products.length === 0 && (<TableRow><TableCell colSpan={ headers.length + 1 }>{ t('common.noResults') }</TableCell></TableRow>) }
                { rows.map(row => (
                  <TableRow key={ row.id }>
                    { row.cells.map(cell => (
                      <TableCell
                        data-label={ cell.info.header }
                        key={ cell.id }
                      >
                        { cell.value }
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
    </StyledContainer>
  );
};

SupplierProductListTable.propTypes = {
  products: array,
};

SupplierProductListTable.displayName = 'SupplierProductListTableComponent';

export default SupplierProductListTable;
